import React from 'react'

import { ZariContext } from '_zari/context'

import { TreeView, Portlet, Row, Col, Button } from '_zari/core'

import { ZrSwal } from 'utils'

import * as CentrosCostoService from 'services/centrosCosto.service'
import * as CompaniaService from 'services/compania.service'

import * as Modals from './modals'

const portletBreadcrumbs = [{ name: 'Centros Costos' }]

const CentrosCostoLista = () => {
  const __zariContext = React.useContext(ZariContext)

  const [centrosCostoLista, setCentrosCostoLista] = React.useState([])
  const [mostrarNuevoModal, setMostrarNuevoModal] = React.useState(false)
  const [mostrarEditarModal, setMostrarEditarModal] = React.useState(false)
  const [mostrarEstructurasModal, setMostrarEstructurasModal] = React.useState(false)

  const editarDataRef = React.useRef(null)
  const selectedNodeIdRef = React.useRef('')
  const idNodoPadreCreacionRef = React.useRef('')
  const nivelCreacionRef = React.useRef(1)
  const maxLengthCreacionRef = React.useRef(0)
  const estructurasRef = React.useRef({})

  const refreshTreeView = React.useCallback(() => {
    CentrosCostoService.getAsTreeView().then(({ data }) => {
      setCentrosCostoLista(data)
    })
  }, [])

  const refreshEstructuras = React.useCallback(() => {
    CompaniaService.getEstructuras()
      .then(({ data }) => {
        if (data) {
          estructurasRef.current = {
            tamanoEstructuraNivel1: data.tamanoEstructuraNivel1 || '',
            tamanoEstructuraNivel2: data.tamanoEstructuraNivel2 || ''
          }
        }
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  React.useEffect(() => {
    refreshTreeView()
    refreshEstructuras()
  }, [refreshTreeView, refreshEstructuras])

  const clickMostrarEstructuras = React.useCallback(() => {
    setMostrarEstructurasModal(true)
  }, [])

  const clickAgregarCentro = React.useCallback(() => {
    if (centrosCostoLista.length > 0) {
      idNodoPadreCreacionRef.current = selectedNodeIdRef.current
      const nodoSeleccionado = centrosCostoLista.find(x => x._id === selectedNodeIdRef.current)
      if (nodoSeleccionado) {
        nivelCreacionRef.current = nodoSeleccionado.nivel + 1
      } else {
        idNodoPadreCreacionRef.current = ''
        nivelCreacionRef.current = 1
      }
    } else {
      idNodoPadreCreacionRef.current = ''
      nivelCreacionRef.current = 1
    }

    if (nivelCreacionRef.current === 1) {
      maxLengthCreacionRef.current = estructurasRef.current.tamanoEstructuraNivel1
    } else if (nivelCreacionRef.current === 2) {
      if (!estructurasRef.current.tamanoEstructuraNivel2) {
        __zariContext.snack('error', 'La estructura está diseñada hasta nivel 1.')
        return
      }
      maxLengthCreacionRef.current = estructurasRef.current.tamanoEstructuraNivel2
    } else {
      __zariContext.snack('error', 'La estructura solo permite 2 niveles.')
      return
    }

    setMostrarNuevoModal(true)
  }, [centrosCostoLista, __zariContext])

  const clickEditar = React.useCallback(() => {
    CentrosCostoService.getCentroCosto(selectedNodeIdRef.current)
      .then(({ data }) => {
        editarDataRef.current = data
        setMostrarEditarModal(true)
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  const clickEliminar = React.useCallback(() => {
    ZrSwal.showAlert({
      mensaje: 'Seguro que desea eliminar el registro?',
      tipo: 'error',
      onSuccess: () => {
        CentrosCostoService.eliminarCentroCosto(selectedNodeIdRef.current)
          .then(() => {
            __zariContext.snack('success', 'El registro se ha eliminado.')
            refreshTreeView()
          })
          .catch(({ data: { error } }) => __zariContext.snack('error', error))
      },
      showCerrar: true
    })
  }, [__zariContext, refreshTreeView])

  const onSelectNode = React.useCallback(nodeId => {
    selectedNodeIdRef.current = nodeId
  }, [])

  return (
    <>
      <Portlet>
        <Portlet.Header
          title='Centros Costo'
          breadcrumbs={portletBreadcrumbs}
          buttons={[{ name: 'Estructuras', onClick: clickMostrarEstructuras }]}
        />
        <Portlet.Body>
          <Row className='mb-2'>
            <Col md={12} className='text-right'>
              <Button outline tiny onClick={clickAgregarCentro}>
                Agregar
              </Button>
              <Button outline tiny onClick={clickEditar}>
                Editar
              </Button>
              <Button color='danger' outline tiny onClick={clickEliminar}>
                Eliminar
              </Button>
            </Col>
          </Row>
          <div className='treeview_wrapper'>
            <TreeView items={centrosCostoLista} onSelect={onSelectNode} />
          </div>
        </Portlet.Body>
      </Portlet>
      {mostrarNuevoModal && (
        <Modals.NuevoModal
          handleClose={e => setMostrarNuevoModal(false)}
          show={mostrarNuevoModal}
          refreshTreeView={refreshTreeView}
          idNodoPadre={idNodoPadreCreacionRef.current}
          nivel={nivelCreacionRef.current}
          maxLength={maxLengthCreacionRef.current}
        />
      )}
      {mostrarEditarModal && (
        <Modals.EditarModal
          handleClose={e => setMostrarEditarModal(false)}
          show={mostrarEditarModal}
          modalData={editarDataRef.current}
          nivel={editarDataRef.current.nivel}
          refreshTreeView={refreshTreeView}
        />
      )}
      {mostrarEstructurasModal && (
        <Modals.EstructurasModal
          handleClose={e => setMostrarEstructurasModal(false)}
          show={mostrarEstructurasModal}
          refreshEstructuras={refreshEstructuras}
        />
      )}
    </>
  )
}

export default React.memo(CentrosCostoLista)
